import { useLanguage } from "@/contexts/LanguageContext";
import { usePublicProjects } from "@/hooks/api/use-public";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, Sparkles, Award, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { getFullImageUrl } from "@/lib/image-utils";

const Portfolio = () => {
  const { t, language } = useLanguage();
  const { data, isLoading, error } = usePublicProjects();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
      {/* Subtle Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/2 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>
      
      {/* Decorative Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />
      
      <div className="relative z-10">
        {/* Enhanced Hero Section */}
        <section className="py-24 md:py-32">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-2xl mb-6 group hover:scale-105 transition-transform duration-300 border border-slate-200">
                  <Building2 className="h-10 w-10 text-primary" />
                </div>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight animate-fade-up">
                {t('portfolio.title') || 'معرض أعمالنا'}
              </h1>
              <p className="text-xl text-slate-600 mb-8 animate-fade-up stagger-1 max-w-3xl mx-auto">
                {t('portfolio.subtitle') || 'نفتخر بمشاريعنا التي نفذناها بتميز واحترافية'}
              </p>
              
              <div className="flex items-center justify-center gap-6 mb-12 animate-fade-up stagger-2">
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
                  <Award size={16} className="text-primary" />
                  <span className="text-slate-700 text-sm font-medium">{t('portfolio.distinguishedProjects') || 'مشاريع متميزة'}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
                  <Sparkles size={16} className="text-primary" />
                  <span className="text-slate-700 text-sm font-medium">{t('portfolio.highQuality') || 'جودة عالية'}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
                  <Shield size={16} className="text-primary" />
                  <span className="text-slate-700 text-sm font-medium">{t('portfolio.comprehensiveGuarantees') || 'ضمانات متكاملة'}</span>
                </div>
              </div>

              <div className="flex items-center justify-center gap-4 animate-fade-up stagger-3">
                <Link 
                  to="/auth?mode=register"
                  className="inline-flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {t('portfolio.joinUs') || 'انضم إلينا'}
                  <Award size={18} />
                </Link>
                <Link 
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-white text-primary px-8 py-3 rounded-xl font-semibold border border-slate-300 hover:bg-slate-50 transition-all duration-300"
                >
                  {t('portfolio.contactUs') || 'تواصل معنا'}
                  <Shield size={18} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Projects Grid */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('portfolio.ourProjects') || 'مشاريعنا'}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {t('portfolio.projectsDescription') || 'استكشف مجموعة من مشاريعنا التي نفذناها بمعايير عالمية'}
              </p>
            </div>

            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    <Skeleton className="h-64" />
                    <div className="p-6">
                      <Skeleton className="h-6 w-3/4 mb-4" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <Shield size={32} className="text-red-600" />
                </div>
                <p className="text-slate-600 text-lg">
                  {t('common.error') || 'حدث خطأ أثناء تحميل المشاريع'}
                </p>
                <button 
                  onClick={() => window.location.reload()}
                  className="mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  {t('common.retry') || 'إعادة المحاولة'}
                </button>
              </div>
            ) : !Array.isArray(data?.data) || data.data.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-4">
                  <Building2 size={32} className="text-slate-400" />
                </div>
                <p className="text-slate-600 text-lg mb-4">
                  {t('portfolio.noProjects') || 'لا توجد مشاريع متاحة حالياً'}
                </p>
                <Link 
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  {t('portfolio.requestProject') || 'اطلب مشروعك'}
                  <Sparkles size={16} />
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {data.data.map((project, index) => (
                  <Link
                    to={`/portfolio/${project.id}`}
                    key={project.id}
                    className="group relative bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border border-slate-200 hover:border-primary/30"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {/* Image Section */}
                    <div className="relative h-64 overflow-hidden">
                      {project.images && project.images.length > 0 ? (
                        <img
                          src={getFullImageUrl(project.images[0].image_url) || ''}
                          alt={typeof project.title === 'string' ? project.title : project.title[language] || project.title.en}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : project.image ? (
                        <img
                          src={getFullImageUrl(project.image) || ''}
                          alt={typeof project.title === 'string' ? project.title : project.title[language] || project.title.en}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : null}
                      {/* Fallback */}
                      {(!project.images || project.images.length === 0) && (!project.image) && (
                        <div className="w-full h-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                          <div className="w-20 h-20 bg-white/80 rounded-full flex items-center justify-center shadow-lg">
                            <Building2 className="w-10 h-10 text-primary" />
                          </div>
                        </div>
                      )}
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      {/* Location Badge */}
                      {project.location && (
                        <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                          <span className="inline-block bg-white/90 backdrop-blur-sm text-primary text-xs font-medium px-3 py-1 rounded-full shadow-lg">
                            {typeof project.location === 'string' ? project.location : project.location[language] || project.location.en}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <div className="mb-4">
                        {project.category && (
                          <span className="inline-block bg-primary/10 text-primary text-xs font-medium px-3 py-1 rounded-full mb-3">
                            {typeof project.category === 'string' ? project.category : project.category[language] || project.category.en}
                          </span>
                        )}
                        <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-primary transition-colors duration-300">
                          {typeof project.title === 'string' ? project.title : project.title[language] || project.title.en}
                        </h3>
                        {project.description && (
                          <p className="text-slate-600 text-sm line-clamp-3">
                            {typeof project.description === 'string' ? project.description : project.description[language] || project.description.en}
                          </p>
                        )}
                      </div>
                      
                      {/* Hover Actions */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Award size={12} />
                          <span>{t('portfolio.viewProject') || 'عرض المشروع'}</span>
                        </div>
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary transition-colors duration-300">
                          <Sparkles size={16} className="text-primary group-hover:text-white transition-colors duration-300" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-16 md:py-24 bg-white/50 backdrop-blur-sm border-t border-slate-200">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {t('portfolio.readyToStart') || 'هل أنت مستعد لبدء مشروعك؟'}
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                {t('portfolio.readyDescription') || 'انضم إلينا اليوم وابدأ رفع مشاريعك لعرضها على عملائك'}
              </p>
              <div className="flex items-center justify-center gap-4">
                <Link 
                  to="/auth?mode=register"
                  className="inline-flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {t('portfolio.getStarted') || 'ابدأ الآن'}
                  <Sparkles size={18} />
                </Link>
                <Link 
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-white text-primary px-8 py-3 rounded-xl font-semibold border border-slate-300 hover:bg-slate-50 transition-all duration-300"
                >
                  {t('portfolio.learnMore') || 'اعرف المزيد'}
                  <Shield size={18} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Portfolio;
